/**
 * Items as icons or as rows (docs/mockups/touch.html, A). The Upgrades, Shard
 * Shop and Museum panels show their items as icon tiles whose name, effect
 * and price live in a tooltip — which a touch screen cannot hover, so a tap
 * there bought an item in the same moment it showed what it was. As rows,
 * each tile carries its words: name, what it does, and the price with its
 * currency's icon.
 *
 * One preference for all three panels, kept in the browser. It starts as
 * rows on a touch screen and icons with a mouse, and Settings changes it —
 * a choice made once, so it lives there rather than beside the tabs. The row text is part of every tile and CSS hides it in icon
 * mode, so buying and affordability work unchanged. The Owned drawers stay
 * icons in both modes: nothing there is bought, and a tap shows the tooltip.
 */

export type Currency = 'eggs' | 'shards' | 'fossils';

const CURRENCY_ICONS: Record<Currency, string> = {
  eggs: 'art/icons/egg.png',
  shards: 'art/icons/shard.png',
  fossils: 'art/icons/fossil.png',
};

const STORAGE_KEY = 'dinoclicker.itemView';
const ROWS_CLASS = 'items-as-rows';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export interface RowText {
  /** Rewrites the name and effect, when they changed (a species can evolve). */
  setText(name: string, description: string): void;
  /** Rewrites the price, for a tile whose price climbs. */
  setCost(amount: string): void;
}

/**
 * Gives a tile on sale its row words. Owned tiles get none: there is nothing
 * to buy, so a tap showing the tooltip is all they need, and they stay icons.
 */
export function addRowText(
  tile: HTMLElement,
  name: string,
  description: string,
  cost?: { currency: Currency; amount: string },
): RowText {
  const text = el('span', 'upgrade__text');
  text.setAttribute('aria-hidden', 'true');
  const nameEl = el('span', 'upgrade__name', name);
  const descEl = el('span', 'upgrade__desc', description);
  text.append(nameEl, descEl);
  tile.append(text);
  let amountEl: HTMLElement | null = null;
  if (cost) {
    const costEl = el('span', 'upgrade__cost');
    costEl.setAttribute('aria-hidden', 'true');
    const icon = el('img', 'upgrade__currency');
    icon.src = `${import.meta.env.BASE_URL}${CURRENCY_ICONS[cost.currency]}`;
    icon.alt = '';
    icon.width = 16;
    icon.height = 16;
    amountEl = el('span', 'upgrade__amount', cost.amount);
    costEl.append(icon, amountEl);
    tile.append(costEl);
  }
  let lastName = name;
  let lastDesc = description;
  let lastAmount = cost?.amount ?? '';
  return {
    setText(n, d) {
      if (n !== lastName) nameEl.textContent = lastName = n;
      if (d !== lastDesc) descEl.textContent = lastDesc = d;
    },
    setCost(amount) {
      if (amountEl && amount !== lastAmount) amountEl.textContent = lastAmount = amount;
    },
  };
}

function storedRows(): boolean {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'rows') return true;
    if (saved === 'icons') return false;
  } catch {
    // No storage: fall through to the device's default.
  }
  // A finger cannot hover, so it gets the words by default.
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

let rows = storedRows();

/** Applies the stored choice to the page; call once at start. */
export function initItemView(): void {
  document.body.classList.toggle(ROWS_CLASS, rows);
}

export function itemViewIsRows(): boolean {
  return rows;
}

/** Sets the preference (from Settings) and remembers it. */
export function setItemViewRows(next: boolean): void {
  rows = next;
  document.body.classList.toggle(ROWS_CLASS, rows);
  try {
    window.localStorage.setItem(STORAGE_KEY, rows ? 'rows' : 'icons');
  } catch {
    // The choice holds for this visit.
  }
}
